import { ChangeDetectionStrategy, Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { DateTimeFormat } from '@y42/date-time';

import { isNotNullish } from '@y42/util/nullish';
import { BehaviorSubject, combineLatest, map, Observable, startWith, switchMap } from 'rxjs';
import { IJobInfoComponent } from '../../api';
import { JobQuery, JobStoreModel } from '../../state';
import { JobRequestDialogService } from '../services';
import { JobInfoService } from './job-info.service';
import { JobLogsDialogService } from './job-logs-dialog/job-logs-dialog.service';
import { JobInfoUIModel, JobLogsSideNavTemplateContext } from './model';

@UntilDestroy()
@Component({
  selector: 'y42-job-info',
  templateUrl: './job-info.component.html',
  styleUrls: ['./job-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [JobRequestDialogService, JobLogsDialogService],
})
export class JobInfoComponent implements IJobInfoComponent {
  constructor(
    private readonly jobQuery: JobQuery,
    private readonly jobInfoService: JobInfoService,
    private readonly jobRequestDialogService: JobRequestDialogService,
    private readonly jobLogDialogService: JobLogsDialogService,
  ) {}

  @Input()
  @InputObservable()
  jobIds: string[] = [];
  jobIds$!: Observable<string[]>;

  @Input()
  showLogsButton = true;

  @ViewChild('jobInfo', { static: true })
  private readonly sideNavTemplate!: TemplateRef<JobLogsSideNavTemplateContext>;

  private readonly selectedJobIndex$ = new BehaviorSubject<number>(0);

  readonly DateTimeFormat = DateTimeFormat;

  readonly state = connectState(this, {
    models: this.jobIds$.pipe(
      switchMap((jobIds) =>
        combineLatest(
          jobIds.map((id) =>
            this.jobQuery
              .selectUIEntity(id)
              .pipe(map((jobUIModel) => (jobUIModel ? this.jobInfoService.toJobInfoUIModel(jobUIModel) : undefined))),
          ),
        ),
      ),
      map((jobInfoModels) => jobInfoModels.filter(isNotNullish).reverse()),
      startWith([]),
    ),
    selectedJobIndex: this.selectedJobIndex$,
  });

  expandJob(index: number, isExpanded: boolean): void {
    if (isExpanded) {
      this.selectedJobIndex$.next(index);
    }
  }

  viewRequest(jobId: JobStoreModel['request_id']) {
    const settingsValidationResult = this.jobQuery.getUIEntity(jobId)?.settingsValidationResult;
    if (!settingsValidationResult) {
      return;
    }

    this.jobRequestDialogService.open(settingsValidationResult);
  }

  openLogsDialog(
    jobId: JobStoreModel['request_id'],
    eventId: Exclude<JobStoreModel['request']['event_id'], undefined>,
  ) {
    this.jobLogDialogService
      .open({
        eventId,
        sideNavTemplate: {
          template: this.sideNavTemplate,
          context: { jobId, eventId },
        },
      })
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  readonly trackById = (index: number, item: JobInfoUIModel) => {
    return item.job?.request_id;
  };
}
